import React from 'react';
import { Building2, FileCheck, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { WizardData } from './index';

interface Step1CompanyProps {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
}

export function Step1Company({ data }: Step1CompanyProps) {
  const { user } = useAuth();

  // Fetch company data
  const { data: company, isLoading } = useQuery({
    queryKey: ['company-for-wizard', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) return null;

      const { data: companyData } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profile.company_id)
        .single();

      return companyData;
    },
    enabled: !!user?.id,
  });

  // Fetch invoice settings to check certificate
  const { data: invoiceSettings } = useQuery({
    queryKey: ['invoice-settings-wizard', company?.id],
    queryFn: async () => {
      if (!company?.id) return null;

      const { data } = await supabase
        .from('invoice_settings')
        .select('*')
        .eq('company_id', company.id)
        .single();

      return data;
    },
    enabled: !!company?.id,
  });

  const formatCnpj = (cnpj: string | null) => {
    if (!cnpj) return '-';
    const digits = cnpj.replace(/\D/g, '');
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const hasCertificate = invoiceSettings?.certificate_file_url;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold">Verificação de Dados</h3>
        <p className="text-sm text-muted-foreground">
          Confirme os dados da empresa e do fornecedor antes de continuar
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Company Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                Sua Empresa (Destinatário)
              </CardTitle>
              {hasCertificate && (
                <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                  <FileCheck className="h-3 w-3 mr-1" />
                  Certificado OK
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </div>
            ) : company ? (
              <>
                <div>
                  <span className="text-sm text-muted-foreground">Razão Social</span>
                  <p className="font-medium">{company.name}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">CNPJ</span>
                  <p className="font-medium font-mono">{formatCnpj(company.cnpj)}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">UF</span>
                  <p className="font-medium">{company.estado || '-'}</p>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">Empresa não encontrada</p>
            )}
          </CardContent>
        </Card>

        {/* Supplier Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-500" />
              Fornecedor (Emitente)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="text-sm text-muted-foreground">Razão Social</span>
              <p className="font-medium">
                {data.invoice.emitente_razao_social || 'Não identificado'}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">CNPJ</span>
              <p className="font-medium font-mono">
                {formatCnpj(data.invoice.emitente_cnpj)}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">UF</span>
              <p className="font-medium">{data.invoice.emitente_uf || '-'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Dados da Nota Fiscal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <span className="text-sm text-muted-foreground">Número</span>
              <p className="font-medium">{data.invoice.invoice_number || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Série</span>
              <p className="font-medium">{data.invoice.invoice_series || '1'}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Data Emissão</span>
              <p className="font-medium">
                {data.invoice.emission_date 
                  ? new Date(data.invoice.emission_date).toLocaleDateString('pt-BR')
                  : '-'}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Valor Total</span>
              <p className="font-medium text-lg">
                {new Intl.NumberFormat('pt-BR', { 
                  style: 'currency', 
                  currency: 'BRL' 
                }).format(data.invoice.total_value || 0)}
              </p>
            </div>
          </div>

          <div className="mt-4">
            <span className="text-sm text-muted-foreground">Chave de Acesso</span>
            <p className="font-mono text-xs break-all">{data.invoice.access_key}</p>
          </div>
        </CardContent>
      </Card>

      {/* Warnings */}
      {!hasCertificate && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Atenção:</strong> Certificado digital não configurado. 
            A manifestação automática pode não funcionar. 
            Configure o certificado em Configurações → Notas Fiscais.
          </AlertDescription>
        </Alert>
      )}

      {company?.estado && data.invoice.emitente_uf && company.estado !== data.invoice.emitente_uf && (
        <Alert>
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription>
            <strong>Operação Interestadual:</strong> O fornecedor está em {data.invoice.emitente_uf} 
            e sua empresa em {company.estado}. O CFOP será ajustado automaticamente.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
